/* eslint-disable max-classes-per-file */
import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import helpers from "../../../../common/utils/utils";
import {
  getVarsoUrl,
  cropNumber,
  areEqual,
} from "../../../../common/helpers/helpers";
import {
  getSchema,
  getVariantResult,
} from "../../../../state/selectors/general.selectors";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import HelperText from "../../../../components/atoms/HelperText";

const coverageShape = PropTypes.shape({
  type: PropTypes.string,
  required: PropTypes.bool,
  read_only: PropTypes.bool,
  label: PropTypes.string,
  items: PropTypes.shape({
    type: PropTypes.string,
  }),
});

const GnomadCoverage = ({ variantResult, schema, title, gnomadType }) => {
  if (!schema || !variantResult) return null;
  let tmpl = null;

  const valuesCoverage =
    gnomadType === "exomes"
      ? variantResult.gnomad_exomes_coverage
      : variantResult.gnomad_genomes_coverage;

  if (schema && variantResult && valuesCoverage) {
    const schemaCoverage =
      gnomadType === "exomes"
        ? schema.properties.gnomad_exomes_coverage
        : schema.properties.gnomad_genomes_coverage;

    const valuesCoverageLen =
      (Array.isArray(valuesCoverage) && valuesCoverage.length) || 0;

    const exacs = [];
    let i = 0;
    const { pos } = variantResult;
    let mean1 = null;
    let median1 = null;
    let freq1 = null;
    let pos1;
    let mean2 = null;
    let median2 = null;
    let freq2 = null;
    let pos2;

    while (i < valuesCoverageLen) {
      const itemCoverage = valuesCoverageLen > i ? valuesCoverage[i] : null;

      // If there are multiple coverage values,
      // calculate the first and last not null triplets
      // and their corresponding positions, and display them.

      let hasMultipleCoverages = false;
      if (
        itemCoverage &&
        itemCoverage.coverage_mean &&
        itemCoverage.coverage_mean.length > 1
      ) {
        for (let idx1 = 0; idx1 < itemCoverage.coverage_mean.length; idx1++) {
          mean1 = itemCoverage.coverage_mean[idx1];
          median1 = itemCoverage.coverage_median[idx1];
          freq1 = itemCoverage.coverage_20_frequency[idx1];
          if (mean1 !== null && median1 !== null && freq1 !== null) {
            pos1 = pos + idx1;
            hasMultipleCoverages = true;
            break;
          }
        }
        if (hasMultipleCoverages) {
          for (
            let idx2 = itemCoverage.coverage_mean.length - 1;
            idx2 >= 0;
            idx2--
          ) {
            mean2 = itemCoverage.coverage_mean[idx2];
            median2 = itemCoverage.coverage_median[idx2];
            freq2 = itemCoverage.coverage_20_frequency[idx2];
            if (mean2 !== null && median2 !== null && freq2 !== null) {
              pos2 = pos + idx2;
              break;
            }
          }
        }
      }
      const helpText =
        gnomadType === "exomes" ? "gnomAD Exomes" : "gnomAD Genomes";

      exacs.push(
        <div key={i} data-testid="exacs-item">
          <div className="varsome-result-fixed">
            <div className="varsome-container varsome-container__basic">
              <ComponentPanel
                version={itemCoverage ? itemCoverage.version : ""}
                url={getVarsoUrl("http://gnomad.broadinstitute.org/")}
                title={title}
                helperPopup={<HelperText fieldDescriptionKey={helpText} />}
                content={
                  <>
                    {itemCoverage && (
                      <div className="varsome-inner-container">
                        <h5>
                          {"Coverage in gnomAD "}
                          {helpers.capitalizeFirstLetter(gnomadType)}
                          {" samples"}
                        </h5>
                        <table className="varsome-table">
                          <thead>
                            <tr>
                              {hasMultipleCoverages && <th>Position</th>}
                              <th>
                                {
                                  schemaCoverage.items.properties.coverage_mean
                                    .label
                                }
                              </th>
                              <th>
                                {
                                  schemaCoverage.items.properties
                                    .coverage_median.label
                                }
                              </th>
                              <th>% of samples over 20x coverage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hasMultipleCoverages && (
                              <tr>
                                <td>{cropNumber(pos1)}</td>
                                <td>{cropNumber(mean1, 1)}</td>
                                <td>{cropNumber(median1)}</td>
                                <td>{cropNumber(freq1 * 100, 2)}%</td>
                              </tr>
                            )}
                            {hasMultipleCoverages && (
                              <tr>
                                <td>{cropNumber(pos2)}</td>
                                <td>{cropNumber(mean2, 1)}</td>
                                <td>{cropNumber(median2)}</td>
                                <td>{cropNumber(freq2 * 100, 2)}%</td>
                              </tr>
                            )}
                            {!hasMultipleCoverages && (
                              <tr>
                                <td>
                                  {cropNumber(itemCoverage.coverage_mean[0], 1)}
                                </td>
                                <td>
                                  {cropNumber(itemCoverage.coverage_median[0])}
                                </td>
                                <td>
                                  {cropNumber(
                                    itemCoverage.coverage_20_frequency[0] * 100,
                                    2
                                  )}
                                  %
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                }
              />
            </div>
          </div>
        </div>
      );
      i += 1;
    }
    tmpl = exacs.slice();
  }

  return tmpl;
};

const mapStateToProps = (state) => ({
  schema: getSchema(state),
  variantResult: getVariantResult(state),
});

GnomadCoverage.propTypes = {
  title: PropTypes.string.isRequired,
  gnomadType: PropTypes.string.isRequired,
  variantResult: PropTypes.shape({
    pos: PropTypes.number,
    gnomad_exomes_coverage: PropTypes.arrayOf(
      PropTypes.shape({
        version: PropTypes.string,
        coverage_mean: PropTypes.arrayOf(PropTypes.number),
        coverage_median: PropTypes.arrayOf(PropTypes.number),
        coverage_20_frequency: PropTypes.arrayOf(PropTypes.number),
      })
    ),
    gnomad_genomes_coverage: PropTypes.arrayOf(
      PropTypes.shape({
        version: PropTypes.string,
        coverage_mean: PropTypes.arrayOf(PropTypes.number),
        coverage_median: PropTypes.arrayOf(PropTypes.number),
        coverage_20_frequency: PropTypes.arrayOf(PropTypes.number),
      })
    ),
  }),
  schema: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    properties: PropTypes.shape({
      gnomad_genomes_coverage: PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        items: PropTypes.shape({
          type: PropTypes.string,
          properties: PropTypes.shape({
            version: PropTypes.shape({
              type: PropTypes.string,
              required: PropTypes.bool,
              read_only: PropTypes.bool,
              label: PropTypes.string,
            }),
            coverage_mean: coverageShape,
            coverage_median: coverageShape,
            coverage_20_frequency: coverageShape,
          }),
        }),
      }),
      gnomad_exomes_coverage: PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        items: PropTypes.shape({
          type: PropTypes.string,
          properties: PropTypes.shape({
            version: PropTypes.shape({
              type: PropTypes.string,
              required: PropTypes.bool,
              read_only: PropTypes.bool,
              label: PropTypes.string,
            }),
            coverage_mean: coverageShape,
            coverage_median: coverageShape,
            coverage_20_frequency: coverageShape,
          }),
        }),
      }),
    }),
  }),
};

GnomadCoverage.defaultProps = {
  variantResult: null,
  schema: null,
};

export default connect(mapStateToProps)(
  React.memo(
    withErrorGuard(GnomadCoverage, "gnomadCoverage", "gnomAD Coverage"),
    areEqual
  )
);
